/**
 * Per-page config for Sales Dashboard Pages (/sales/dashboard-pages/*).
 * Header profiles mirror reference Topbar() visibility rules.
 */

/** Temporary: hide PIQ/HubSpot sync pills until real sync is implemented. */
export const SALES_DASHBOARD_SYNC_WIDGETS_ENABLED = false

/** Temporary: hide Open Pipeline 6-month Forecast chart until ready. */
export const SALES_DASHBOARD_OPEN_PIPELINE_FORECAST_ENABLED = false

/** Temporary: hide dashboard pages from nav and direct routes until ready. */
export const SALES_DASHBOARD_HIDDEN_PAGE_SLUGS: readonly string[] = [
  'open-pipeline',
  'amazon-sales',
  'texas-sales-by-rep',
  'amazon-invoiced-by-state',
  'customer-mapping',
  'custom-reports',
]

/** Temporary: hide entire Dashboard Pages sidebar sections until ready. */
export const SALES_DASHBOARD_HIDDEN_NAV_SECTIONS: readonly string[] = [
  'dpAmazon',
  'dpMyReports',
]

export function isDashboardPageVisible(slug: string): boolean {
  return !SALES_DASHBOARD_HIDDEN_PAGE_SLUGS.includes(slug)
}

export function isDashboardPageNavSectionVisible(sectionKey: string): boolean {
  return !SALES_DASHBOARD_HIDDEN_NAV_SECTIONS.includes(sectionKey)
}

export type SdViewModeKind = 'hidden' | 'tree-flat' | 'amazon' | 'source-created-invoiced'

export interface SalesDashboardHeaderProfile {
  sync: boolean
  viewMode: SdViewModeKind
  dateRange: boolean
  filter: boolean
  export: boolean
}

export interface DashboardPageConfig {
  title: string
  subtitle?: string
  headerProfile: SalesDashboardHeaderProfile
  /** Reference route id used by session composable */
  referenceRoute: string
  isAdmin?: boolean
  isAmazon?: boolean
  isPerformance?: boolean
  isPipeline?: boolean
  lockedCustomReportId?: string
}

export const DASHBOARD_PAGES: Record<string, DashboardPageConfig> = {
  performance: {
    title: 'Performance',
    subtitle: 'Pacing & trends · select month above',
    referenceRoute: 'performance',
    isPerformance: true,
    headerProfile: { sync: true, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
  leaderboard: {
    title: 'Leaderboard',
    subtitle: 'Rep stack-ranking · quota progress',
    referenceRoute: 'leaderboard',
    headerProfile: { sync: true, viewMode: 'source-created-invoiced', dateRange: true, filter: true, export: true },
  },
  'open-pipeline': {
    title: 'Open Pipeline',
    subtitle: 'HubSpot deal pipelines · quoted $',
    referenceRoute: 'pipeline',
    isPipeline: true,
    headerProfile: { sync: true, viewMode: 'hidden', dateRange: false, filter: true, export: false },
  },
  'sale-detail': {
    title: 'Sales Detail',
    subtitle: 'Team → customer → rep · roll-up totals',
    referenceRoute: 'sale-detail',
    headerProfile: {
      sync: true,
      viewMode: 'tree-flat',
      dateRange: true,
      filter: true,
      export: true,
    },
  },
  'amazon-dashboard': {
    title: 'Amazon Dashboard',
    subtitle: 'Fulfillment sites · rank by site or state',
    referenceRoute: 'amazon',
    isAmazon: true,
    headerProfile: {
      sync: true,
      viewMode: 'amazon',
      dateRange: true,
      filter: true,
      export: true,
    },
  },
  'inbound-orders': {
    title: 'Inbound Quotes/Jobs',
    subtitle: 'Recent quotes and accepted orders awaiting shipment',
    referenceRoute: 'inbound-orders',
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
  'amazon-sales': {
    title: 'Amazon Sales',
    subtitle: 'Rank by site code / state · drill to orders',
    referenceRoute: 'amazon',
    isAmazon: true,
    headerProfile: { sync: true, viewMode: 'amazon', dateRange: true, filter: true, export: true },
  },
  'texas-sales-by-rep': {
    title: 'Texas — sales by rep',
    subtitle: 'Custom report',
    referenceRoute: 'custom:cr-tx-rep',
    lockedCustomReportId: 'cr-tx-rep',
    headerProfile: { sync: true, viewMode: 'tree-flat', dateRange: true, filter: true, export: true },
  },
  'amazon-invoiced-by-state': {
    title: 'Amazon — invoiced by state',
    subtitle: 'Custom report',
    referenceRoute: 'custom:cr-amz-state',
    lockedCustomReportId: 'cr-amz-state',
    isAmazon: true,
    headerProfile: { sync: true, viewMode: 'amazon', dateRange: true, filter: true, export: true },
  },
  budgets: {
    title: 'Budgets',
    subtitle: 'Monthly created / invoiced targets',
    referenceRoute: 'budgets',
    isAdmin: true,
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: true },
  },
  'teams-and-reps': {
    title: 'Teams & Reps',
    subtitle: 'Per-rep quotas · commission %',
    referenceRoute: 'teams-admin',
    isAdmin: true,
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: true },
  },
  'customer-mapping': {
    title: 'Customer Mapping',
    subtitle: 'Hubspot ↔ PIQ reconciliation',
    referenceRoute: 'customer-mapping',
    isAdmin: true,
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: true },
  },
  'custom-reports': {
    title: 'Custom Reports',
    subtitle: 'Custom report builder',
    referenceRoute: 'custom-reports',
    isAdmin: true,
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: true },
  },
  'sales-workflow-new-product': {
    title: 'New Product',
    subtitle: 'Sales intake · sign builder',
    referenceRoute: 'sales-workflow-new-product',
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
  'sales-workflow-modify': {
    title: 'Modify Existing Product',
    subtitle: 'Sales intake · product changes',
    referenceRoute: 'sales-workflow-modify',
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
  'sales-workflow-product-bundle': {
    title: 'Product Bundle',
    subtitle: 'Heading to Product Development',
    referenceRoute: 'sales-workflow-product-bundle',
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
  'sales-workflow-new-customer-order': {
    title: 'New Customer Order',
    subtitle: 'Heading to Estimating',
    referenceRoute: 'sales-workflow-new-customer-order',
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
  'sales-workflow-drafts': {
    title: 'My Drafts',
    subtitle: 'Saved sales intake requests',
    referenceRoute: 'sales-workflow-drafts',
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
  'sales-workflow-submitted': {
    title: 'Submitted Requests',
    subtitle: "Everything you've sent through Origin",
    referenceRoute: 'sales-workflow-submitted',
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
  'sales-workflow-submission-detail': {
    title: 'Submission detail',
    subtitle: 'Full request payload',
    referenceRoute: 'sales-workflow-submission-detail',
    headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
  },
}

function deslugify(slug: string): string {
  return slug
    .split('-')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ')
}

export function getDashboardPageConfig(slug: string): DashboardPageConfig {
  return (
    DASHBOARD_PAGES[slug] ?? {
      title: deslugify(slug),
      referenceRoute: slug,
      headerProfile: { sync: false, viewMode: 'hidden', dateRange: false, filter: false, export: false },
    }
  )
}

export function dashboardPageTitle(slug: string): string {
  return DASHBOARD_PAGES[slug]?.title ?? deslugify(slug)
}
